import cronstrue from "cronstrue";
import { JobsConfigurationJob } from "modules/system/sections/plt/field-management/rings/rollout-package/configure/jobs/dtos";
import { ReadableCronDisplay } from "modules/system/sections/plt/field-management/rings/rollout-package/configure/jobs/ReadableCronDisplay";

import {
  IStackProps,
  Stack,
  SubmissionForm,
  TextInputField,
  useFieldArray,
  useTheme
} from "@bps/fluent-ui";
import { ValidationSchema, Validator } from "@bps/validator";

export interface JobFormValues extends Omit<JobsConfigurationJob, "commands"> {}

export const jobFormValidator = new Validator<JobFormValues>();

interface JobFormProps extends Omit<IStackProps, "onSubmit"> {
  initialValues: JobFormValues;
  onSubmit: (values: JobFormValues) => Promise<void>;
  onDismiss: () => void;
}

export const JobForm: React.FC<JobFormProps> = ({
  initialValues,
  onSubmit,
  onDismiss
}) => {
  const theme = useTheme();

  const { value: jobs } = useFieldArray<JobsConfigurationJob>("jobs");

  const jobFormSchema: ValidationSchema<JobFormValues> = {
    name: [
      jobFormValidator.string().maxLength(255, {
        message: "Maximum length is 255 characters"
      }),
      jobFormValidator.string().required({ message: "Name is required." })
    ],
    code: [
      jobFormValidator.custom().func<"code">((_value, values) => {
        if (
          jobs.some(x => !!x.code && x.code === _value && x.id !== values.id)
        ) {
          return `Code '${_value}' is already in use.`;
        } else return _value;
      }),
      jobFormValidator.string().required({ message: "Code is required." })
    ],
    executionFrequency: [
      jobFormValidator
        .string()
        .required({ message: "Execution frequency is required." }),
      jobFormValidator.custom().func<"executionFrequency">(value => {
        try {
          cronstrue.toString(value);
          return value;
        } catch {
          return "Invalid cron expression";
        }
      })
    ]
  };

  return (
    <SubmissionForm<JobFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={values => jobFormValidator.validate(values, jobFormSchema)}
      cancelButtonProps={{ text: "Close", onClick: onDismiss }}
    >
      {() => (
        <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
          <TextInputField label="Name" required name="name" />
          <TextInputField label="Code" required name="code" />

          <Stack
            tokens={{ childrenGap: 4 }}
            styles={{ root: { minWidth: "450px" } }}
          >
            <TextInputField
              label="Execution Frequency"
              required
              name="executionFrequency"
            />
            <ReadableCronDisplay field="executionFrequency" />
          </Stack>
        </Stack>
      )}
    </SubmissionForm>
  );
};
